import { importDuplicatesSchema, importPreviewSchema, inquiryImportReportSchema, assignedInquirySchema, createInquirySchema, createdInquirySchema, extractedInquirySchema, inquiryContactsSchema, inquiryListPageSchema, inquiryStatusBreakdownSchema, inquirySchema, reminderPageSchema, } from '@ghp/domain';
import { bearer } from './http.js';
import { queryString } from './query-string.js';
/**
 * Les demandes vues du bureau (D21, D24) : le « Tableau des demandes », son export, l'écran
 * « Relances », et les deux écritures qui s'y déclenchent — créer une demande, l'affecter à une
 * équipe. Le jeton voyage **en argument**, comme la synchro : celui de la session en cours, et
 * rien d'autre.
 */
export function createInquiriesEndpoints(http) {
    return {
        /**
         * Une page du tableau — filtrée, triée et **comptée par le serveur**.
         *
         * ⚠️ Pas `/inquiries`, qui est la **tournée d'une équipe** : la confondre rendrait une page
         * vide à un gestionnaire, son jeton ne portant aucune équipe.
         */
        list(accessToken, request = {}) {
            return http.get(`/inquiries/back-office${queryString(request)}`, inquiryListPageSchema, bearer(accessToken));
        },
        /**
         * Une page de l'écran « Relances » : les demandes en retard, leurs motifs et les quatre
         * compteurs — filtrée, ordonnée et comptée par le serveur.
         *
         * ⚠️ **La réponse dépend de l'instant où le serveur l'a servie**, contrairement aux autres
         * listes : les échéances tombent. Deux appels identiques peuvent donc rendre deux pages
         * différentes, et une clé de cache doit vieillir comme telle.
         */
        reminders(accessToken, request = {}) {
            return http.get(`/inquiries/reminders${queryString(request)}`, reminderPageSchema, bearer(accessToken));
        },
        /**
         * La répartition des demandes par position du cycle de vie, pour le tableau de bord.
         *
         * ⚠️ **Les sept statuts arrivent toujours**, `dropped` compris : le contrat les exige tous
         * (`inquiryStatusBreakdownSchema`), et une réponse amputée est refusée ici plutôt que de
         * laisser l'écran lire un statut absent comme « aucune demande ».
         */
        statusCounts(accessToken, clientId = 'all') {
            // `'all'` n'entre pas dans l'URL : c'est le défaut du serveur, et l'écrire ferait deux
            // adresses pour une même lecture — donc deux entrées de cache pour une seule réponse.
            const query = clientId === 'all' ? '' : `?clientId=${encodeURIComponent(clientId)}`;
            return http.get(`/inquiries/status-counts${query}`, inquiryStatusBreakdownSchema, bearer(accessToken));
        },
        /**
         * Crée une demande depuis le bureau — la demande, son site et son adresse, en une transaction
         * côté serveur.
         *
         * ⚠️ **Ce qui revient n'est pas que la demande** : `duplicateReferences` dit combien en
         * portaient déjà la même « Réf. GHP ». Zéro dans le cas courant ; au-delà, l'écran a quelque
         * chose à signaler — la création, elle, a bel et bien eu lieu (M1, garde-fou n°3). L'ignorer
         * reviendrait à taire le seul avertissement que cette route émet.
         */
        create(accessToken, request) {
            return http.postJson('/inquiries', request, createdInquirySchema, bearer(accessToken));
        },
        /**
         * Lit un PDF de demande et rend de quoi **pré-remplir** l'écran de création
         * ([D7](../../../docs/ARCHITECTURE.md#d7)).
         *
         * ⚠️ **Rien n'est créé, et rien n'est conservé** : la réponse est une demande non persistée.
         * C'est `create()` — donc un humain qui a relu — qui écrit.
         *
         * ⚠️ **Le type est imposé, pas déduit du fichier.** Un PDF choisi depuis un poste qui n'a pas
         * su deviner son type arrive avec un `type` vide ; l'en-tête manquant vaudrait un `415` que
         * le gestionnaire ne peut ni comprendre ni corriger, alors que le document est bon. La route
         * n'accepte de toute façon que celui-là, et c'est le lecteur PDF qui tranche sur les octets.
         *
         * ⚠️ **Un refus se reconnaît à son statut** : `413` (trop de pages), `400` (illisible ou
         * vide), `415` (type refusé). L'`ApiError` porte les trois, et le corps porte la phrase que
         * le serveur a écrite — elle dit ce que l'utilisateur peut faire, la réécrire la jetterait.
         */
        extract(accessToken, document) {
            return http.postBinaryJson('/inquiries/extraction', document, extractedInquirySchema, {
                ...bearer(accessToken),
                'content-type': 'application/pdf',
            });
        },
        /**
         * Affecte une demande à une équipe — le geste qui la fait descendre dans la tournée.
         *
         * ⚠️ La demande rendue est **celle que le serveur a écrite**, statut compris : c'est la seule
         * façon pour l'appelant de savoir si le cycle a avancé. Renvoyer ce qu'il a envoyé
         * réafficherait `assigned` sur un contrôle que le serveur a laissé `in_progress`.
         */
        assign(accessToken, inquiryId, request) {
            return http.postJson(`/inquiries/${inquiryId}/assign`, request, inquirySchema, bearer(accessToken));
        },
        /** Abandonne une demande (#505) : `dropped`, sans la supprimer. Rend la demande écrite. */
        drop(accessToken, inquiryId) {
            return http.postJson(`/inquiries/${inquiryId}/drop`, {}, inquirySchema, bearer(accessToken));
        },
        /**
         * Corrige la « Réf. GHP » d'une demande (#406). Rend la demande et le nombre d'**autres**
         * demandes qui portent la référence : un doublon se signale, il n'est pas refusé.
         */
        update(accessToken, inquiryId, request) {
            return http.patchJson(`/inquiries/${inquiryId}`, request, createdInquirySchema, bearer(accessToken));
        },
        /**
         * L'agent **en ligne** remplace les coordonnées d'une demande de sa tournée — le contact du
         * rendez-vous (avec sa qualité), le propriétaire, ou les deux — et reçoit la **ligne de
         * tournée** à jour, à reposer telle quelle dans le cache de la tablette.
         *
         * ⚠️ **Jamais par l'outbox.** C'est une écriture directe, hors proposition (seconde exception
         * à D4) : elle ne s'offre que connectée, et un échec se dit à l'agent au lieu de se rejouer
         * plus tard sur une demande qui aura pu changer d'équipe ou partir au bureau.
         *
         * ⚠️ **N'envoyer que ce que l'agent a touché** : un champ absent reste tel qu'il est en base,
         * `null` ou `''` l'efface. Envoyer la fiche entière effacerait ce que la tablette n'affiche pas.
         *
         * ⚠️ **Quatre refus à afficher mot pour mot** : `403` (demande d'une autre équipe, ou jeton
         * sans équipe), `409` (la demande a quitté la tournée — transmise, validée…), `404` (inconnue),
         * `400` (correction sans nom alors qu'aucune personne n'est encore enregistrée à ce titre).
         */
        updateContacts(accessToken, inquiryId, request) {
            return http.patchJson(`/inquiries/${inquiryId}/contacts`, request, assignedInquirySchema, bearer(accessToken));
        },
        /**
         * Les coordonnées d'une demande pour « Gérer une demande » (#407) : demandeur, personne
         * présente et sa qualité, propriétaire, et les rôles qu'une même personne tient.
         */
        contacts(accessToken, inquiryId) {
            return http.get(`/inquiries/back-office/${inquiryId}/contacts`, inquiryContactsSchema, bearer(accessToken));
        },
        /**
         * Le brouillon de la contre-visite d'une demande (#321) : la boîte de création s'ouvre dessus,
         * et l'envoie telle quelle à `create` une fois relue.
         */
        /** La demande telle que « Modifier la demande » la rouvre (#434). */
        draft(accessToken, inquiryId) {
            return http.get(`/inquiries/back-office/${inquiryId}/draft`, createInquirySchema, bearer(accessToken));
        },
        counterVisitDraft(accessToken, inquiryId) {
            return http.get(`/inquiries/back-office/${inquiryId}/counter-visit`, createInquirySchema, bearer(accessToken));
        },
        /**
         * Le bureau corrige ces coordonnées (#407) : même corps que la tablette — un champ absent reste
         * tel quel —, sans borne d'équipe ni de statut. Rend les coordonnées à jour.
         */
        correctContacts(accessToken, inquiryId, request) {
            return http.patchJson(`/inquiries/back-office/${inquiryId}/contacts`, request, inquiryContactsSchema, bearer(accessToken));
        },
        /**
         * Le premier temps de l'**import d'une liste** (xlsx ou CSV) : les feuilles importables, leurs
         * colonnes et la correspondance proposée. Rien ne s'écrit ; `415` pour un autre format, `400`
         * au-delà de 5 000 lignes.
         */
        previewImport(accessToken, file, fileName) {
            const form = new FormData();
            form.append('file', file, fileName);
            return http.postFormJson('/inquiries/imports/preview', form, importPreviewSchema, bearer(accessToken));
        },
        /**
         * Le second temps : **le même fichier**, et la feuille, la correspondance, le client, le type
         * et le lot choisis. Rend le rapport — une ligne par ligne du tableur, doublons **créés et
         * signalés**, erreurs nommées (M21).
         *
         * ⚠️ Le fichier se renvoie : le serveur ne le garde pas entre les deux temps.
         */
        importList(accessToken, file, fileName, request) {
            const form = new FormData();
            form.append('file', file, fileName);
            form.append('request', JSON.stringify(request));
            return http.postFormJson('/inquiries/imports', form, inquiryImportReportSchema, bearer(accessToken));
        },
        /**
         * Entre la correspondance et l'écriture (#589) : **le même fichier et la même demande**, et
         * les lignes en doublon, avec ce qu'elles doublent. Rien ne s'écrit.
         */
        importDuplicates(accessToken, file, fileName, request) {
            const form = new FormData();
            form.append('file', file, fileName);
            form.append('request', JSON.stringify(request));
            return http.postFormJson('/inquiries/imports/duplicates', form, importDuplicatesSchema, bearer(accessToken));
        },
        /**
         * L'export du tableau : les **mêmes** filtres que la liste, toutes les lignes, pas la page —
         * ou, avec `ids`, les seules demandes cochées (#587), que le serveur rend à la place du filtre.
         *
         * ⚠️ **`GET`, pas `POST`.** Contrairement à l'export client, cet appel n'écrit rien — pas
         * de trace. C'est une lecture du tableau sous une autre forme.
         *
         * ⚠️ **`limit` et `offset` sont écartés ici**, même s'ils figurent dans `InquiryListRequest` :
         * ce sont les bornes de la page affichée, et les envoyer ferait croire au journal qu'on a
         * demandé une tranche. Le serveur les ignore de toute façon.
         *
         * ⚠️ **Une sélection vide ne part pas** : l'URL n'écrit pas un paramètre vide, et le serveur
         * lirait alors « pas de sélection » — tout le tableau.
         */
        export(accessToken, request = {}) {
            const { limit: _limit, offset: _offset, ids, ...filters } = request;
            if (ids?.length === 0) {
                return Promise.reject(new RangeError('aucune demande cochée : rien à exporter'));
            }
            return http.getFile(`/inquiries/back-office/export${queryString({ ...filters, ids: ids?.join(',') })}`, bearer(accessToken));
        },
    };
}
//# sourceMappingURL=inquiries.js.map